import React, { createRef, useMemo } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import cx from 'classnames';

import Slugger from 'github-slugger';
import { renderLinkElement } from '@plone/volto-slate/editor/render';
import config from '@plone/volto/registry';
import withQuerystringResults from '@plone/volto/components/manage/Blocks/Listing/withQuerystringResults';
import { normalizeString } from '@plone/volto/helpers/Utils/Utils';
/* START CUSTOMIZATION */
import useDeepCompareEffect from 'use-deep-compare-effect';
import withFilter from '@fhnw/components/Blocks/Listing/withFilter';
import { Loader } from '@designsystem/stories/02-atoms/Loader';
import { ButtonGroup } from '@designsystem/stories/03-molecules/ButtonGroup';
import { Message } from '@designsystem/stories/03-molecules/Message';
import { messages } from '@fhnw/components/Blocks/Listing/messages';
/* END CUSTOMIZATION */

const Headline = ({ headlineTag, id, data = {}, listingItems, isEditMode }) => {
  let attr = { id };
  const slug = Slugger.slug(normalizeString(data.headline));
  attr.id = slug || id;
  const LinkedHeadline = useMemo(
    () => renderLinkElement(headlineTag),
    [headlineTag],
  );
  return (
    <LinkedHeadline
      mode={!isEditMode && 'view'}
      children={data.headline}
      attributes={attr}
      className={cx('headline', {
        emptyListing: !listingItems?.length > 0,
      })}
    />
  );
};

const ListingBody = withFilter(
  withQuerystringResults((props) => {
    const {
      data = {},
      isEditMode,
      onPaginationChange,
      variation,
      currentPage,
      isFolderContentsListing,
      hasLoaded,
      id,
    } = props;

    let ListingBodyTemplate;
    // Legacy support if template is present
    const variations = config.blocks?.blocksConfig['listing']?.variations || [];
    const defaultVariation = variations.filter((item) => item.isDefault)?.[0];

    /* START CUSTOMIZATION */
    const [listingItems, setListingItems] = React.useState(
      props.listingItems || [],
    );
    const [totalPages, setTotalPages] = React.useState(props.totalPages || 0);
    useDeepCompareEffect(() => {
      if (hasLoaded && listingItems !== props.listingItems) {
        setListingItems(props.listingItems || []);
      }
    }, [props.listingItems, hasLoaded]);
    React.useEffect(() => {
      if (hasLoaded) {
        setTotalPages(props.totalPages || 0);
      }
    }, [props.totalPages, hasLoaded]);
    /* END CUSTOMIZATION */

    if (data.template && !data.variation) {
      const legacyTemplateConfig = variations.find(
        (item) => item.id === data.template,
      );
      ListingBodyTemplate = legacyTemplateConfig.template;
    } else {
      ListingBodyTemplate =
        variation?.template ?? defaultVariation?.template ?? null;
    }

    const listingRef = createRef();

    const NoResults = variation?.noResultsComponent
      ? variation.noResultsComponent
      : config.blocks?.blocksConfig['listing'].noResultsComponent;
    /* START CUSTOMIZATION */
    const intl = props.intl;
    const HeadlineTag = 'h3';

    const handlePaginationChange = (e, { activePage }) => {
      onPaginationChange(e, { activePage });
      if (listingRef?.current) {
        listingRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    };

    const paginationItem = (page) => ({
      label: `${page}`,
      onClick: () => {
        handlePaginationChange(null, { activePage: page });
      },
    });

    let paginationItems = [];
    if (
      props?.batch_size > 0 &&
      props?.batch_size !== Infinity &&
      totalPages > 0 &&
      totalPages !== Infinity
    ) {
      if (currentPage > 1) {
        paginationItems.push({
          title: intl.formatMessage(messages.PreviousPage),
          type: 'plain',
          icon: 'chevron_left',
          onClick: () => {
            handlePaginationChange(null, { activePage: currentPage - 1 });
          },
        });
      }
      let page = 1;
      while (page < 2 && page < currentPage && page < totalPages) {
        paginationItems.push(paginationItem(page++));
      }
      if (page < currentPage - 1) {
        paginationItems.push({
          label: '…',
          type: 'plain',
        });
      }
      page = Math.max(page, currentPage - 1);
      while (page < currentPage) {
        paginationItems.push(paginationItem(page++));
      }
      paginationItems.push({
        type: 'primary',
        label: currentPage,
      });
      page = currentPage + 1;
      while (page < currentPage + 2 && page < totalPages) {
        paginationItems.push(paginationItem(page++));
      }
      if (page < totalPages) {
        paginationItems.push({
          label: '…',
          type: 'plain',
        });
      }
      page = Math.max(page, totalPages);
      while (page <= totalPages) {
        paginationItems.push(paginationItem(page++));
      }
      if (currentPage < totalPages) {
        paginationItems.push({
          title: intl.formatMessage(messages.NextPage),
          type: 'plain',
          icon: 'chevron_right',
          onClick: () => {
            handlePaginationChange(null, { activePage: currentPage + 1 });
          },
        });
      }
    }

    /* END CUSTOMIZATION */
    return (
      <>
        {data.headline && (
          <Headline
            headlineTag={HeadlineTag}
            id={id}
            listingItems={listingItems}
            data={data}
            isEditMode={isEditMode}
          />
        )}
        {listingItems?.length > 0 ? (
          <div ref={listingRef} className="listing__wrapper">
            {/* START CUSTOMIZATION */}
            {!hasLoaded && <Loader className="listing__loader" />}
            {/* END CUSTOMIZATION */}
            <ListingBodyTemplate
              items={listingItems}
              isEditMode={isEditMode}
              {...data}
              {...variation}
            />
            {/* START CUSTOMIZATION */}
            {paginationItems?.length > 0 && (
              <ButtonGroup
                className="listing__pagination"
                size="small"
                items={paginationItems}
              />
            )}
            {/* END CUSTOMIZATION */}
          </div>
        ) : isEditMode ? (
          <div className="listing__empty" ref={listingRef}>
            {isFolderContentsListing && (
              <Message type="info">
                <FormattedMessage
                  id="No items found in this container."
                  defaultMessage="No items found in this container."
                />
              </Message>
            )}
            {hasLoaded && NoResults && (
              <Message type="info">
                <NoResults isEditMode={isEditMode} {...data} />
              </Message>
            )}
            {!hasLoaded && (
              <Loader
                message={intl.formatMessage({
                  id: 'loading',
                  defaultMessage: 'Loading',
                })}
              />
            )}
          </div>
        ) : (
          <div className="listing__empty">
            {hasLoaded && NoResults && (
              <Message type="info">
                <NoResults isEditMode={isEditMode} {...data} />
              </Message>
            )}
            {!hasLoaded && (
              <Loader
                message={intl.formatMessage({
                  id: 'loading',
                  defaultMessage: 'Loading',
                })}
              />
            )}
          </div>
        )}
      </>
    );
  }),
);

export default injectIntl(ListingBody);
